import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import moment from "moment";

import FetchModule from '../../Model/Share/Network/FetchModule';
import ChannelTalk from '../../Model/Share/Network/ChannelTalk';
import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import TotalModalWrapper from '../../Model/Share/Normal/TotalModalWrapper';

import MembershipSelectPanel from "./MembershipSelectPanel";
import EditableProvisionCard from "./EditableProvisionCard";
import ProvisionSelectPanel from "./ProvisionSelectPanel";
import ProvisionDeleteModal from "./ProvisionDeleteModal";

export default function TermsEditModal(props) {
  const {
    storeName,
    clickedTerms,
    provisions,
    setProvisions,
    setTerms,
    defaultProvisions,
    memberships,
    categories,
    onClickExit,
    onClickSave
  } = props;

  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [id, setId] = useState(undefined);
  const [title, setTitle] = useState('');
  const [provisionKeys, setProvisionKeys] = useState([]);

  // 적용 회원권 선택 — isAllMembershipsSelected=true && selectedIds=[] 이면 저장시 [] (전체 적용) //
  const [isAllMembershipsSelected, setIsAllMembershipsSelected] = useState(true);
  const [selectedMembershipIds, setSelectedMembershipIds] = useState([]);

  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [isTitleError, setIsTitleError] = useState(false);

  // 발행 전 임시 상태 — 발행하기 클릭시 일괄 처리 //
  const [draftProvisions, setDraftProvisions] = useState([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [selectedProvisionKey, setSelectedProvisionKey] = useState(undefined);

  const [clickedDeleteProvision, setClickedDeleteProvision] = useState(undefined);
  const [isPublishing, setIsPublishing] = useState(false);

  const tempIdCounter = useRef(0);
  const centerScrollRef = useRef(null);

  // Channel Talk 끄기/켜기 //
  useEffect(() => {
    ChannelTalk.shutdown();

    return () => {
      ChannelTalk.boot({ "pluginKey": "d2f4dfce-95e8-498e-aed3-84804b9d3772" });
    }
  }, [])

  // clickedTerms 변경시 입력값/드래프트 초기화 //
  useEffect(() => {
    if (clickedTerms === undefined) {
      setId(undefined);
      setTitle('');
      setProvisionKeys([]);
      setIsAllMembershipsSelected(true);
      setSelectedMembershipIds([]);
    }
    else {
      setId(clickedTerms.id);
      setTitle(clickedTerms.title);
      setProvisionKeys(clickedTerms.provisionKeys ? clickedTerms.provisionKeys.slice() : []);
      // membershipIds: undefined/null/[] → 전체 적용, [...] → 해당 회원권만 적용 //
      const restoredIds = clickedTerms.membershipIds;
      if (restoredIds === undefined || restoredIds === null || restoredIds.length === 0) {
        setIsAllMembershipsSelected(true);
        setSelectedMembershipIds([]);
      }
      else {
        setIsAllMembershipsSelected(false);
        setSelectedMembershipIds(restoredIds.slice());
      }
    }
    setDraftProvisions(provisions.map((p) => ({ ...p })));
    setPendingDeleteIds([]);
    setSelectedProvisionKey(undefined);
    setIsTitleError(false);
    tempIdCounter.current = 0;
  }, [clickedTerms])

  // 드래프트에서 삭제 보류분 제외 //
  const visibleProvisions = draftProvisions.filter((p) => !pendingDeleteIds.includes(p.id));

  // provisionKeys 순서대로 포함된 규정 객체 배열 (드래프트 기준) //
  const includeProvisions = provisionKeys
    .map((key) => visibleProvisions.find((p) => p.id === key))
    .filter((p) => p !== undefined);

  // 신규 임시 id 판정 //
  function isTempId(provisionId) {
    return typeof provisionId === 'string' && provisionId.startsWith('temp-');
  }

  // 다음 "추가 규정 N" 기본 제목 //
  function nextAddedProvisionTitle() {
    const pattern = /^추가 규정\s*(\d+)$/;
    let maxN = 0;
    draftProvisions.forEach((p) => {
      const match = typeof p.title === 'string' ? p.title.match(pattern) : null;
      if (match) maxN = Math.max(maxN, parseInt(match[1], 10));
    });
    return `추가 규정${maxN + 1}`;
  }

  // 좌측 — 적용 회원권 전체 토글 //
  function onClickAllMemberships() {
    if (isAllMembershipsSelected) {
      setIsAllMembershipsSelected(false);
    }
    else {
      setIsAllMembershipsSelected(true);
      setSelectedMembershipIds([]);
    }
  }

  // 좌측 — 개별 회원권 토글 //
  function onClickMembershipItem(membershipId) {
    // 전체 선택 상태에서 개별 클릭 → 전체를 개별 선택으로 펼친 뒤 클릭한 항목만 해제 //
    if (isAllMembershipsSelected) {
      const allIds = (Array.isArray(memberships) ? memberships : []).map(m => m.id);

      setIsAllMembershipsSelected(false);
      setSelectedMembershipIds(allIds.filter(id => id !== membershipId));
      return;
    }

    const next = selectedMembershipIds.slice();
    const idx = next.indexOf(membershipId);

    if (idx > -1) next.splice(idx, 1);
    else next.push(membershipId);

    // 개별 선택이 전체 목록과 같아지면 전체 선택 상태로 되돌림 //
    const allIds = (Array.isArray(memberships) ? memberships : []).map(m => m.id);

    if (allIds.length > 0 && next.length === allIds.length) {
      setIsAllMembershipsSelected(true);
      setSelectedMembershipIds([]);
      return;
    }

    setSelectedMembershipIds(next);
  }

  // 우측 패널 — 항목 토글 //
  function onToggleProvision(data) {
    const keys = provisionKeys.slice();
    const idx = keys.indexOf(data.id);

    if (idx > -1) keys.splice(idx, 1);
    else keys.push(data.id);

    setProvisionKeys(keys);
  }

  // 중앙 — 카드 위로 이동 //
  function onMoveUp(index) {
    if (index <= 0) return;

    const keys = provisionKeys.slice();
    [keys[index - 1], keys[index]] = [keys[index], keys[index - 1]];

    setProvisionKeys(keys);
  }

  // 중앙 — 카드 아래로 이동 //
  function onMoveDown(index) {
    if (index >= provisionKeys.length - 1) return;

    const keys = provisionKeys.slice();
    [keys[index], keys[index + 1]] = [keys[index + 1], keys[index]];

    setProvisionKeys(keys);
  }

  // 중앙 — 카드 제거 //
  function onRemoveProvision(index) {
    const keys = provisionKeys.slice();

    keys.splice(index, 1);

    setProvisionKeys(keys);
  }

  // 우측 — 규정 추가하기: 본문 맨 앞에 빈 카드 즉시 추가 (발행 전까지 로컬 보관) + 스크롤 최상단 //
  function onClickAddProvision() {
    tempIdCounter.current += 1;
    const tempId = `temp-${Date.now()}-${tempIdCounter.current}`;
    const newProvision = {
      id: tempId,
      title: nextAddedProvisionTitle(),
      text: ''
    };
    setDraftProvisions((prev) => [newProvision, ...prev]);
    setProvisionKeys((prev) => [tempId, ...prev]);
    setSelectedProvisionKey(tempId);

    if (centerScrollRef.current) {
      centerScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // 카드 선택 (편집 모드 진입) //
  function onSelectProvision(provisionKey) {
    setSelectedProvisionKey(provisionKey);
  }

  // 카드 제목 변경 //
  function onChangeProvisionTitle(provisionId, newTitle) {
    setDraftProvisions((prev) => prev.map((p) => p.id === provisionId ? { ...p, title: newTitle } : p));
  }

  // 카드 본문 변경 //
  function onChangeProvisionText(provisionId, newText) {
    setDraftProvisions((prev) => prev.map((p) => p.id === provisionId ? { ...p, text: newText } : p));
  }

  // 우측 — 규정 삭제 클릭 (확인 모달 진입) //
  function onClickDeleteProvision(data) {
    setClickedDeleteProvision(data);
  }

  // 규정 삭제 확인 모달 닫기 //
  function onClickExitDeleteProvision() {
    setClickedDeleteProvision(undefined);
  }

  // 규정 삭제 (발행 전까지 보류) //
  function onClickExcuteDeleteProvision() {
    if (clickedDeleteProvision === undefined) return;
    const targetId = clickedDeleteProvision.id;

    if (isTempId(targetId)) {
      // 로컬 임시 규정 — 드래프트에서 제거
      setDraftProvisions((prev) => prev.filter((p) => p.id !== targetId));
    }
    else {
      // 서버 존재 규정 — 보류 목록에 추가
      setPendingDeleteIds((prev) => prev.includes(targetId) ? prev : [...prev, targetId]);
    }
    setProvisionKeys((prev) => prev.filter((k) => k !== targetId));
    if (selectedProvisionKey === targetId) setSelectedProvisionKey(undefined);
    setClickedDeleteProvision(undefined);
  }

  // 상단 — 발행하기: provision/save → provision/delete → terms/save 순차 일괄 처리 //
  function onClickSaveButton() {
    if (isPublishing) {
      alert(t('myStoreSetting.terms.termsSaving'));
      return;
    }
    if (title === '') {
      setIsTitleError(true);
      if (centerScrollRef.current) centerScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      alert(t('myStoreSetting.terms.termsTitleRequired'));
      return;
    }
    if (provisionKeys.length === 0) {
      alert(t('myStoreSetting.terms.termsProvisionRequired'));
      return;
    }
    if (!isAllMembershipsSelected && selectedMembershipIds.length === 0) {
      alert(t('myStoreSetting.terms.membershipRequired'));
      return;
    }

    // 변경/신규 규정 추출 (사용중인 임시 규정 + 본문 수정된 기존 규정) //
    const originalById = new Map(provisions.map((p) => [p.id, p]));
    const provisionsToSave = draftProvisions
      .filter((p) => !pendingDeleteIds.includes(p.id))
      .filter((p) => {
        if (isTempId(p.id)) return provisionKeys.includes(p.id);
        const original = originalById.get(p.id);
        return original !== undefined && (original.title !== p.title || original.text !== p.text);
      });

    setIsPublishing(true);

    const fetchModule = new FetchModule();

    (
      async () => {
        let latestProvisions = provisions.slice();
        let currentIds = new Set(latestProvisions.map((p) => p.id));
        const idMapping = {};

        try {
          // 1. 신규/수정 규정 순차 저장 //
          for (const p of provisionsToSave) {
            const isNew = isTempId(p.id);
            const requestData = {
              id: isNew ? undefined : p.id,
              title: p.title,
              text: p.text
            };
            const resp = await fetchModule.postDataVersion2('terms/provision/save', 'POST', requestData);

            if (resp.status !== 200) {
              alert(t('myStoreSetting.terms.provisionSaveFailed'));
              setIsPublishing(false);
              return;
            }

            latestProvisions = resp.data.provisions;

            if (isNew) {
              const newOnes = latestProvisions.filter((x) => !currentIds.has(x.id));
              if (newOnes.length > 0) idMapping[p.id] = newOnes[newOnes.length - 1].id;
            }

            currentIds = new Set(latestProvisions.map((x) => x.id));
          }

          // 2. 보류된 삭제 처리 (임시 id는 서버에 없으므로 제외) //
          for (const delId of pendingDeleteIds) {
            if (isTempId(delId)) continue;

            const resp = await fetchModule.postDataVersion2('terms/provision/delete', 'POST', { id: delId });

            if (resp.status === 200) {
              latestProvisions = resp.data.provisions;
            }
            else if (resp.status === 201) {
              alert(t('myStoreSetting.terms.deleteProvisionAlreadyDeleted'));
              setIsPublishing(false);
              return;
            }
            else if (resp.status === 202) {
              alert(t('myStoreSetting.terms.deleteProvisionDefault'));
              setIsPublishing(false);
              return;
            }
            else if (resp.status === 203) {
              alert(t('myStoreSetting.terms.deleteProvisionInUse'));
              setIsPublishing(false);
              return;
            }
            else {
              alert(t('myStoreSetting.terms.deleteProvisionFailed'));
              setIsPublishing(false);
              return;
            }
          }

          // 부모 provisions 갱신 + 로컬 상태 임시→실 id 매핑 //
          if (provisionsToSave.length > 0 || pendingDeleteIds.some((d) => !isTempId(d))) {
            setProvisions(latestProvisions);
          }

          const mappedKeys = provisionKeys.map((k) => idMapping[k] !== undefined ? idMapping[k] : k);

          setProvisionKeys(mappedKeys);
          setDraftProvisions(latestProvisions.map((p) => ({ ...p })));
          setPendingDeleteIds([]);

          // 3. terms/save — 부모에서 처리 //
          const termData = {
            id: id,
            title: title,
            provisionKeys: mappedKeys,
            membershipIds: isAllMembershipsSelected ? [] : selectedMembershipIds,
            status: id === undefined ? false : clickedTerms.status
          };

          onClickSave(termData);
        }
        catch (err) {
          alert(t('myStoreSetting.terms.termsSaveFailedAlert'));
        }
        finally {
          setIsPublishing(false);
        }
      }
    )();
  }

  // 중앙 — 포함된 규정 카드 렌더 //
  function renderIncludeCards() {
    return includeProvisions.map((data, index) => (
      <EditableProvisionCard
        key={'IncludeProvision-' + data.id}
        data={data}
        index={index}
        lastIndex={includeProvisions.length - 1}
        isSelected={selectedProvisionKey === data.id}
        onSelect={() => onSelectProvision(data.id)}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onRemove={onRemoveProvision}
        onChangeTitle={onChangeProvisionTitle}
        onChangeText={onChangeProvisionText} />
    ));
  }

  // 중앙 — 기본 규정(개인정보/광고성) 렌더 //
  function renderDefaultProvisions() {
    return defaultProvisions.map((data, index) => (
      <DefaultProvisionWrapper key={'DefaultProvision-' + index}>
        <SectionLabel fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{data.title}</SectionLabel>
        <PreText fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{data.text.replaceAll('{storeName}', storeName)}</PreText>
      </DefaultProvisionWrapper>
    ));
  }

  return (
    <TotalModalWrapper>
      {
        clickedDeleteProvision !== undefined &&
        <ProvisionDeleteModal
          onClickExit={onClickExitDeleteProvision}
          onClickExcuteDeleteProvision={onClickExcuteDeleteProvision} />
      }
      <Modal>
        <InfoWrapper>
          <TopBar>
            <TopTitleText fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{id === undefined ? t('myStoreSetting.terms.editTermsCreate') : t('myStoreSetting.terms.editTermsModify')}</TopTitleText>
            <TopButtonGroup>
              <CloseButton onClick={onClickExit}>
                <CloseButtonText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.close')}</CloseButtonText>
              </CloseButton>
              <PublishButton onClick={onClickSaveButton}>
                <PublishButtonText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subBackground}>{t('myStoreSetting.terms.publish')}</PublishButtonText>
              </PublishButton>
            </TopButtonGroup>
          </TopBar>

          <BodyRow>
            <MembershipSelectPanel
              memberships={memberships}
              categories={categories}
              isAllSelected={isAllMembershipsSelected}
              selectedIds={selectedMembershipIds}
              onClickAll={onClickAllMemberships}
              onClickItem={onClickMembershipItem} />

            <CenterScroll ref={centerScrollRef}>
              <TitleBoxWrapper isError={isTitleError}>
                <TitleInput
                  isError={isTitleError}
                  value={title}
                  placeholder={isTitleFocused ? '' : t('myStoreSetting.terms.termsTemplateName')}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (isTitleError) setIsTitleError(false);
                  }}
                  onFocus={() => setIsTitleFocused(true)}
                  onBlur={() => setIsTitleFocused(false)} />
              </TitleBoxWrapper>

              <CenterTitleRow>
                <CenterTitleText fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{'[' + storeName + ']' + t('myStoreSetting.terms.membershipContract')}</CenterTitleText>
              </CenterTitleRow>

              <DivisionLine backgroundColor={theme.line} />

              <SectionLabel fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.terms.memberInfo')}</SectionLabel>
              <MemberInfoRow>
                <MemberInfoCell>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.terms.name')}</Text>
                  <MemberInfoBox />
                </MemberInfoCell>
                <MemberInfoCell>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.terms.gender')}</Text>
                  <MemberInfoBox />
                </MemberInfoCell>
                <MemberInfoCell>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.terms.contact')}</Text>
                  <MemberInfoBox />
                </MemberInfoCell>
              </MemberInfoRow>

              <DivisionLine backgroundColor={theme.line} />

              <SectionLabel fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.terms.termsOfUse')}</SectionLabel>
              {renderIncludeCards()}
              {renderDefaultProvisions()}

              <DivisionLine backgroundColor={theme.line} />
              
              <SignRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{moment().format(t('myStoreSetting.terms.signDateFormat'))}</Text>
                <SignBoxGroup>
                  <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{t('myStoreSetting.terms.memberName')}</Text>
                  <SignBox>
                    <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{t('myStoreSetting.terms.sign')}</Text>
                  </SignBox>
                </SignBoxGroup>
              </SignRow>
              <StoreNameRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{storeName}</Text>
              </StoreNameRow>
            </CenterScroll>

            <ProvisionSelectPanel
              provisions={visibleProvisions}
              provisionKeys={provisionKeys}
              onToggle={onToggleProvision}
              onClickDelete={onClickDeleteProvision}
              onClickAdd={onClickAddProvision} />
          </BodyRow>
        </InfoWrapper>
      </Modal>
    </TotalModalWrapper>
  )
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 1280px;
  max-width: calc(100vw - 80px);

  height: calc(100vh - 80px);
  min-height: 600px;
  max-height: 900px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  box-sizing: border-box;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  padding: 20px;

  width: 100%;
  height: 100%;

  gap: 10px;

  box-sizing: border-box;
`;

const TopBar = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 40px;
`;

const TopTitleText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};
`;

const TopButtonGroup = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 10px;
`;

const CloseButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 62px;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const CloseButtonText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};
`;

const PublishButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0 16px;

  height: 40px;
  width: 170px;

  border-radius: 5px;
  background-color: ${props => props.theme.primary};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const PublishButtonText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};
`;

const BodyRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: stretch;

  padding: 10px 0;

  width: 100%;
  flex: 1 0 0;
  min-height: 0;

  gap: 30px;
`;

const CenterScroll = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  padding: 10px;

  flex: 1 0 0;
  min-height: 0;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  overflow-x: hidden;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 1px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.primary};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const TitleBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: calc(100% - 4px);
  height: 40px;
  flex-shrink: 0;

  border: 2px dotted ${props => props.isError ? props.theme.pointRed : props.theme.line};
  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};

  box-sizing: border-box;
`;

const TitleInput = styled.input`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  margin: 0;
  padding: 0;

  width: 80%;
  height: 30px;

  text-align: center;

  outline: none;
  border: none;
  background-color: transparent;
  color: ${props => props.theme.text};

  &::placeholder {
    color: ${props => props.isError ? props.theme.pointRed : props.theme.line};
  }
`;

const CenterTitleRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 8px 10px;

  width: calc(100% - 20px);
`;

const CenterTitleText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};
`;

const DivisionLine = styled.div`
  width: 100%;
  height: 2px;

  background-color: ${props => props.backgroundColor};
  opacity: 0.3;

  flex-shrink: 0;
`;

const SectionLabel = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;
  padding: 10px;

  color: ${props => props.color};
`;

const MemberInfoRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 0 10px 10px 10px;

  width: calc(100% - 20px);

  gap: 10px;
`;

const MemberInfoCell = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;
`;

const MemberInfoBox = styled.div`
  width: 90px;
  height: 20px;

  border-bottom: 1px dashed ${props => props.theme.line};
`;

const DefaultProvisionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const SignRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 0 10px;

  width: calc(100% - 20px);
  height: 38px;
`;

const SignBoxGroup = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;
`;

const SignBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 148px;
  height: 30px;

  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill};
`;

const StoreNameRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin: 10px 0 40px 0;

  width: 100%;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};
`;

const PreText = styled.pre`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;
  padding: 0 10px;

  width: calc(100% - 20px);

  white-space: pre-wrap;

  color: ${props => props.color};
`;
